import { scoreSheetIsClientTotal } from "./scoreSheetScoringModel";

// Card #3334 -- moved verbatim out of hooks/useScoreSheet.js so the sheet
// resend (local/sheetManifest.js) builds its body with the SAME stripping rules
// as an ordinary save, without importing a React hook module.

// score-api owns these; never send them back on a save (the server recomputes
// totals on complete and stamps its own timestamps/id/org).
export const SERVER_MANAGED = [
  "id",
  "orgId",
  "createdAt",
  "updatedAt",
  "totalPoints",
  "maxPoints",
  "percentage",
  // #1751 — panel signatures are written ONLY by POST /scoresheet/:id/
  // panel-signature (score-api lists `signatures` in SERVER_ONLY_FIELDS, so a
  // PUT could never set them anyway). signPanel() merges an OPTIMISTIC entry
  // into local state with `signatureHash: null` — only the server knows the
  // real S3 content address — and that entry would fail the sheet schema if a
  // debounced save carried it back up.
  "signatures",
  // #2756 — the local-cache-only flag on persist()'s provisional
  // entityKey-keyed row. Nothing server-side knows it; belt-and-braces strip
  // so it can never reach a create/update body even if a cache row ever
  // leaked into sheet state.
  "provisional",
  // #3008 -- request-only field toBody() below sets explicitly from the
  // tracked ancestor (_syncedSheet), never carried through from sheet state
  // itself (which may still carry a previous call's value via the local
  // cache-row merge persist() does after enqueueing).
  "baseVersion",
  // #3008 judge round 2 (B2) -- the local cache row's tracked ancestor.
  // Sheet state seeded from a cache row (fromCache) carries it, so without
  // this strip every save PUT the whole previous ancestor to score-api as
  // an unknown field; an echoing response then nested it inside the next
  // ancestor stamp and wedged the row as permanently "diverged".
  "_syncedSheet",
  // Card 3466 -- score-api's held-for-review marker on a 200 reply (the save
  // was held for the Review Queue, #3439). The cached row keeps it so a later
  // server read does not replace the tablet's marks (db.js), but it is never
  // part of a save body.
  "heldForReview",
];

// Map the local sheet to a create/update body: drop server-managed + undefined
// keys. The remaining shape matches score-common's scoreSheetSchema.
// `test` is the resolved Test for this sheet (may be null while still
// loading) — passed so percentage can be let through for the one scoring
// model that owns it client-side (see scoreSheetIsClientTotal above).
export function toBody(sheet, test, ancestor) {
  const clientTotal = scoreSheetIsClientTotal(test);
  const body = {};
  for (const [k, v] of Object.entries(sheet)) {
    if (k === "percentage" && clientTotal) {
      if (v !== undefined) body[k] = v;
      continue;
    }
    if (SERVER_MANAGED.includes(k)) continue;
    if (v !== undefined) body[k] = v;
  }
  // #3008 -- score-api's stale_write check reads a request-only baseVersion
  // field, never the stored `version` (stripped above; never refreshed
  // client side otherwise). `ancestor` is the local row's tracked
  // `_syncedSheet` -- the sheet exactly as last returned by the server, the
  // boundary THIS edit is actually based on. Only set when one exists: an
  // unsynced create has none yet, and omitting it skips the server's check
  // entirely (back-compat, every other client still omits it today).
  if (ancestor?.version !== undefined) body.baseVersion = ancestor.version;
  return body;
}
